import { Injectable, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { ScrollToTopService } from 'src/app/shared/services/scroll-to-top.service';
import { UserSettingsService } from './user-settings.service';
import { UserSettingStore } from './user-setting-store.base';
import { coerceListScroll, LIST_SCROLL_KEY, ListScrollPref } from './list-scroll.util';

/**
 * App-wide cache of the user's list-scroll preference: whether paging or sorting a list brings the
 * table back to the top (the default) or leaves the viewport where it was.
 *
 * The value is APPLIED by pushing it into ScrollToTopService rather than having that service read
 * it. ScrollToTopService is injected by TableComponent, TableEditRowsComponent and every ingest
 * list; making it depend on this store would pull Store + NgZone + UserSettingsService into the
 * TestBed of all of them. Pushing keeps the leaf service dependency-free, and its `enabled` default
 * means the documented behaviour holds before the per-identity load lands.
 *
 * The cross-tab channel, the auth-gated load and the optimistic write live in UserSettingStore
 * (shared with DateFormatService / NumberFormatService / ToastDurationService).
 */
@Injectable({ providedIn: 'root' })
export class ListScrollService extends UserSettingStore {
  private readonly listScroll = new BehaviorSubject<ListScrollPref>('top');
  readonly listScroll$: Observable<ListScrollPref> = this.listScroll.asObservable();

  get currentListScroll(): ListScrollPref {
    return this.listScroll.value;
  }

  constructor(
    userSettings: UserSettingsService,
    store: Store,
    zone: NgZone,
    private scrollToTop: ScrollToTopService,
  ) {
    super(userSettings, store, zone);
  }

  setListScroll(value: ListScrollPref): void {
    this.setPref(this.listScroll, LIST_SCROLL_KEY, value, 'top');
    this.apply(value);
  }

  protected load(userId: number): void {
    this.userSettings
      .getAllSharedStrict(userId)
      .pipe(take(1))
      .subscribe({
        next: settings => this.applyAndEmit(coerceListScroll(settings[LIST_SCROLL_KEY])),
        error: () => this.rearmLoad(userId),
      });
  }

  protected override handlesKey(key?: string): boolean {
    return key === LIST_SCROLL_KEY;
  }

  protected onBroadcast(data: { key?: string; value?: unknown }): void {
    if (data?.key === LIST_SCROLL_KEY) {
      this.applyAndEmit(coerceListScroll(data.value));
    }
  }

  // `emit` skips a no-op subject emission, but the applied value must be set unconditionally: on the
  // first load the subject already holds the default, so a stored 'top' would emit nothing and leave
  // ScrollToTopService untouched -- harmless today (it defaults to enabled) but a silent coupling to
  // that default. Applying every time keeps the service and the subject in step.
  private applyAndEmit(value: ListScrollPref): void {
    this.apply(value);
    this.emit(this.listScroll, value);
  }

  private apply(value: ListScrollPref): void {
    this.scrollToTop.setEnabled(value === 'top');
  }
}
